import { Form, Input, Select, InputNumber, Button, Card, Row, Col, notification, Switch, Table, Popconfirm, Tabs } from "antd";
import { useEffect, useState, useRef, useContext, createContext } from "react";
import { DeleteOutlined } from "@ant-design/icons";
import { NomenclatureAutoComplete } from "src/shared";
import Compact from "antd/es/space/Compact";
import NomenclatureSearchField from "src/shared/api/NomenclatureSearchField";
import "./style.css";

const { TextArea } = Input;
const { Option } = Select;

const EditableContext = createContext(null);

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr className="editable-row" {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({ 
  title, 
  editable, 
  children, 
  dataIndex, 
  record, 
  handleSave, 
  inputType = 'input',
  inputOptions = {},
  ...restProps 
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
      } catch (errInfo) {
    }
  };

  const handleBlur = () => {
    save();
  };

  let childNode = children;

  if (editable) {
    const isEditing = editing;
    childNode = isEditing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} обязательно для ввода.`,
          },
        ]}
      >
        {inputType === 'select' ? (
          <Select
            ref={inputRef}
            onChange={(value) => {
              form.setFieldsValue({ [dataIndex]: value });
              setTimeout(() => {
                save();
              }, 0);
            }}
            onBlur={handleBlur}
            style={{ width: "100%" }}
            {...inputOptions}
          >
            {inputOptions.options?.map(opt => (
              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
            ))}
          </Select>
        ) : inputType === 'number' ? (
          <InputNumber
            ref={inputRef}
            onPressEnter={save}
            onBlur={handleBlur}
            min={inputOptions.min || 0}
            step={inputOptions.step || 1}
            style={{ width: "100%" }}
          />
        ) : (
          <Input
            ref={inputRef}
            onPressEnter={save}
            onBlur={handleBlur}
          />
        )}
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
          cursor: 'pointer',
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

export default function TechCardsForm({
  formContext,
  record,
  parent,
  api,
  token,
  setChangeData,
  onSelectNom,
  nomenclatureNames,
  nomenclatureData: initialNomenclatureData,
}) {
  const [localNomenclatureNames, setLocalNomenclatureNames] = useState(nomenclatureNames || {});
  const [nomenclatureData, setNomenclatureData] = useState(initialNomenclatureData || {});
  const [activeTab, setActiveTab] = useState("main");
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (initialNomenclatureData && Object.keys(initialNomenclatureData).length > 0) {
      setNomenclatureData(initialNomenclatureData);
    } else if (record && record.items) {
      const initialNames = { ...nomenclatureNames };
      const initialData = {};
      record.items.forEach((item, index) => {
        if (item.nomenclature_id && !initialNames[index]) {
          initialNames[index] = item.nomenclature_name || item.nomenclature_id;
          initialData[item.nomenclature_id] = {
            id: item.nomenclature_id,
            name: item.nomenclature_name || "Неизвестно",
            type: item.type || "",
            tags: item.tags || [],
            description_short: item.description_short || "",
            description_long: item.description_long || "",
            code: item.code || "",
            unit_name: item.unit_name || "",
            category_name: item.category_name || "",
            manufacturer_name: item.manufacturer_name || "",
            cashback_type: item.cashback_type || "",
            cashback_percent: item.cashback_percent || 0,
          };
        }
      });
      setLocalNomenclatureNames(initialNames);
      setNomenclatureData(initialData);
    }
  }, [record, nomenclatureNames, initialNomenclatureData]);

  const calculateGrossWeight = (netWeight, coldWaste, heatWaste) => {
    const net = parseFloat(netWeight || 0);
    const cold = parseFloat(coldWaste || 0);
    const heat = parseFloat(heatWaste || 0);
    const baseWeight = net + cold + heat;
    const adjustedWeight = baseWeight < 0.01 ? 0.01 : baseWeight;
    return parseFloat(adjustedWeight.toFixed(3));
  };

  const updateGrossWeight = (index) => {
    const items = formContext.getFieldValue("items") || [];
    const item = items[index];

    if (item) {
      const grossWeight = calculateGrossWeight(
        item.net_weight || 0.1,
        item.waste_from_cold_processing || 0.01,
        item.waste_from_heat_processing || 0.01
      );

      formContext.setFieldsValue({ 
        [`items.${index}.gross_weight`]: grossWeight 
      });
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    if (setChangeData) setChangeData(true);

    if (changedValues.items) {
      const changedIndex = Object.keys(changedValues.items)[0];
      if (changedIndex !== undefined) {
        const changedField = Object.keys(changedValues.items[changedIndex])[0];

        if (
          changedField &&
          (changedField === "net_weight" ||
            changedField === "waste_from_cold_processing" ||
            changedField === "waste_from_heat_processing")
        ) {
          setTimeout(() => {
            updateGrossWeight(parseInt(changedIndex));
          }, 0);
        }
      }
    }
  };

  const initializeItemWeights = (items) => {
    return items.map((item) => ({
      ...item,
      waste_from_cold_processing: item.waste_from_cold_processing || 0.01,
      waste_from_heat_processing: item.waste_from_heat_processing || 0.01,
      gross_weight: calculateGrossWeight(
        item.net_weight || 0.1,
        item.waste_from_cold_processing || 0.01,
        item.waste_from_heat_processing || 0.01
      ),
    }));
  };

  return (
    <Form
      form={formContext}
      initialValues={{
        card_type: "reference",
        auto_produce: false,
        status: record?.status === "active" || false,
        ...record,
        items: record?.items ? initializeItemWeights(record.items) : [],
      }}
      layout="vertical"
      onValuesChange={handleValuesChange}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "main",
            label: "Основное",
            children: (
              <Card size="small" title="Основная информация" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Название"
                  name="name"
                  rules={[{ required: true, message: "Введите название техкарты" }]}
                >
                  <Input placeholder="Введите название техкарты" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Тип карты"
                  name="card_type"
                  rules={[{ required: true, message: "Выберите тип карты" }]}
                >
                  <Select>
                    <Option value="reference">Эталонная</Option>
                    <Option value="automatic">Автоматическая</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Код" name="code">
                  <Input placeholder="Введите код" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Родительская карта" name="parent">
                  <Select
                    options={parent}
                    placeholder="Выберите родительскую техкарту"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Статус" name="status" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Описание" name="description">
              <TextArea
                rows={3}
                placeholder="Введите описание техкарты"
                style={{ resize: "vertical" }}
              />
            </Form.Item>

            <Form.Item name="auto_produce" hidden>
              <Input type="hidden" />
            </Form.Item>
              </Card>
            ),
          },
          {
            key: "composition",
            label: "Состав техкарты",
            children: (
              <Form.List name="items">
        {(fields, { add, remove }) => {
          const handleAutoAddNomenclature = async (val) => {
            if (isProcessingRef.current) {
              return;
            }
            
            isProcessingRef.current = true;
            
            try {
              const response = await fetch(`${api}nomenclature/${val}/?token=${token}`);
              if (!response.ok) throw new Error("Failed to fetch nomenclature");
              const data = await response.json();
              
              if (!data.id) {
                notification.error({ message: "Ошибка", description: "Номенклатура не найдена" });
                isProcessingRef.current = false;
                return;
              }

              const items = formContext.getFieldValue("items") || [];
              const existingIndex = items.findIndex(
                (item) => item.nomenclature_id === Number(data.id)
              );

              if (existingIndex !== -1) {
                const updatedItems = [...items];
                updatedItems[existingIndex] = {
                  ...updatedItems[existingIndex],
                  quantity: (updatedItems[existingIndex].quantity || 1) + 1,
                };
                formContext.setFieldsValue({ items: updatedItems });
              } else {
                const newItem = {
                  nomenclature_id: Number(data.id),
                  type_of_processing: "cutting",
                  net_weight: data.net_weight || 0.1,
                  waste_from_cold_processing: 0.01,
                  waste_from_heat_processing: 0.01,
                  quantity: 1,
                  gross_weight: calculateGrossWeight(
                    data.net_weight || 0.1,
                    0.01,
                    0.01
                  ),
                  output: data.output || 0.1,
                };

                const newIndex = fields.length;
                add(newItem);
                
                setNomenclatureData((prev) => ({
                  ...prev,
                  [data.id]: {
                    id: data.id,
                    name: data.name || "Неизвестно",
                    type: data.type || "",
                    tags: data.tags || [],
                    description_short: data.description_short || "",
                    description_long: data.description_long || "",
                    code: data.code || "",
                    unit_name: data.unit_name || "",
                    category_name: data.category_name || "",
                    manufacturer_name: data.manufacturer_name || "",
                    cashback_type: data.cashback_type || "",
                    cashback_percent: data.cashback_percent || 0,
                  },
                }));
                
                setTimeout(() => {
                  setLocalNomenclatureNames((prev) => ({
                    ...prev,
                    [newIndex]: data.name || "Неизвестно",
                  }));
                }, 0);
              }
              
              setTimeout(() => {
                formContext.setFieldsValue({
                  nomenclature_search: undefined,
                  nomenclature_button: undefined,
                  nomenclature_search_field: undefined,
                });
                isProcessingRef.current = false;
              }, 200);
              
              if (setChangeData) setChangeData(true);
            } catch (error) {
              notification.error({
                message: "Ошибка",
                description: "Произошла ошибка при добавлении номенклатуры",
              });
              isProcessingRef.current = false;
            }
          };

          const handleSave = (row) => {
            const items = formContext.getFieldValue("items") || [];
            const index = row.index;
            
            if (index !== undefined && index < items.length) {
              const updatedItems = [...items];
              const currentItem = updatedItems[index];
              
              const netWeightChanged = row.net_weight !== undefined && row.net_weight !== currentItem.net_weight;
              const coldWasteChanged = row.waste_from_cold_processing !== undefined && row.waste_from_cold_processing !== currentItem.waste_from_cold_processing;
              const heatWasteChanged = row.waste_from_heat_processing !== undefined && row.waste_from_heat_processing !== currentItem.waste_from_heat_processing;
              const grossWeightChanged = row.gross_weight !== undefined && row.gross_weight !== currentItem.gross_weight;
              
              if ((netWeightChanged || coldWasteChanged || heatWasteChanged) && !grossWeightChanged) {
                const newNetWeight = row.net_weight !== undefined ? row.net_weight : currentItem.net_weight;
                const newColdWaste = row.waste_from_cold_processing !== undefined ? row.waste_from_cold_processing : currentItem.waste_from_cold_processing;
                const newHeatWaste = row.waste_from_heat_processing !== undefined ? row.waste_from_heat_processing : currentItem.waste_from_heat_processing;
                
                row.gross_weight = calculateGrossWeight(
                  newNetWeight || 0.1,
                  newColdWaste || 0.01,
                  newHeatWaste || 0.01
                );
              }
              
              updatedItems[index] = { 
                ...currentItem, 
                ...row,
              };
              
              formContext.setFieldsValue({ items: updatedItems });
              
              if (setChangeData) setChangeData(true);
            }
          };

          const tableData = fields.map((field, index) => {
            const item = formContext.getFieldValue(["items", index]) || {};
            const nomData = nomenclatureData[item.nomenclature_id] || {};
            const itemName = localNomenclatureNames[index] || nomData.name || "Неизвестно";
            
            return {
              key: field.key,
              index: index,
              nomenclature_id: item.nomenclature_id,
              name: itemName,
              type_of_processing: item.type_of_processing || "cutting",
              quantity: item.quantity || 1,
              net_weight: item.net_weight || 0.1,
              waste_from_cold_processing: item.waste_from_cold_processing || 0.01,
              waste_from_heat_processing: item.waste_from_heat_processing || 0.01,
              output: item.output || 0.1,
              gross_weight: item.gross_weight || calculateGrossWeight(
                item.net_weight || 0.1,
                item.waste_from_cold_processing || 0.01,
                item.waste_from_heat_processing || 0.01
              ),
            };
          });

          const columns = [
            {
              title: "Номенклатура",
              dataIndex: "name",
              key: "name",
              width: 200,
            },
            {
              title: "Тип обработки",
              dataIndex: "type_of_processing",
              key: "type_of_processing",
              width: 200,
              editable: true,
              inputType: 'select',
              inputOptions: {
                options: [
                  { value: "cutting", label: "Нарезка" },
                  { value: "cooking", label: "Термическая обработка" },
                  { value: "other", label: "Другое" },
                ],
              },
              render: (value) => {
                const typeMap = {
                  "cutting": "Нарезка",
                  "cooking": "Термическая обработка",
                  "other": "Другое",
                };
                return typeMap[value] || value;
              },
            },
            {
              title: "Количество",
              dataIndex: "quantity",
              key: "quantity",
              width: 120,
              editable: true,
              inputType: 'number',
              inputOptions: { min: 1, step: 1 },
            },
            {
              title: "Вес нетто (кг)",
              dataIndex: "net_weight",
              key: "net_weight",
              width: 130,
              editable: true,
              inputType: 'number',
              inputOptions: { min: 0.01, step: 0.01 },
            },
            {
              title: "Отх. хол. (кг)",
              dataIndex: "waste_from_cold_processing",
              key: "waste_from_cold_processing",
              width: 130,
              editable: true,
              inputType: 'number',
              inputOptions: { min: 0.01, step: 0.01 },
            },
            {
              title: "Отх. тепл. (кг)",
              dataIndex: "waste_from_heat_processing",
              key: "waste_from_heat_processing",
              width: 130,
              editable: true,
              inputType: 'number',
              inputOptions: { min: 0.01, step: 0.01 },
            },
            {
              title: "Выход (кг)",
              dataIndex: "output",
              key: "output",
              width: 120,
              editable: true,
              inputType: 'number',
              inputOptions: { min: 0.01, step: 0.01 },
            },
            {
              title: "Вес брутто (кг)",
              dataIndex: "gross_weight",
              key: "gross_weight",
              width: 140,
              editable: true,
              inputType: 'number',
              inputOptions: { min: 0.01, step: 0.01 },
            },
            {
              title: "Действие",
              key: "action",
              width: 100,
              render: (_, record) => {
                const fieldToRemove = fields.find(f => f.key === record.key);
                return (
                  <Popconfirm
                    title="Подтвердите удаление"
                    onConfirm={() => {
                      if (fieldToRemove) {
                        remove(fieldToRemove.name);
                        setLocalNomenclatureNames((prev) => {
                          const newNames = { ...prev };
                          delete newNames[record.index];
                          return newNames;
                        });
                        if (setChangeData) setChangeData(true);
                      }
                    }}
                    cancelText="Отмена"
                    okText="OK"
                  >
                    <Button icon={<DeleteOutlined />} danger size="small" />
                  </Popconfirm>
                );
              },
            },
          ];

          const components = {
            body: {
              row: EditableRow,
              cell: EditableCell,
            },
          };

          const editableColumns = columns.map((col) => {
            if (!col.editable) {
              return col;
            }
            return {
              ...col,
              onCell: (record) => ({
                record,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
                handleSave: handleSave,
                inputType: col.inputType,
                inputOptions: col.inputOptions,
              }),
            };
          });

          return (
          <Card
            size="small"
            title={
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <span>Состав техкарты</span>
              </div>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Form.Item
                name="nomenclature_search"
                style={{ marginBottom: 0 }}
              >
                <Compact style={{ width: "100%" }}>
                  <Form.Item
                    name="nomenclature_button"
                    style={{ width: "15%", marginBottom: 0, marginInlineEnd: 0 }}
                  >
                    <NomenclatureAutoComplete
                      buttonIntegrated={true}
                      api={api}
                      token={token}
                      onSelect={handleAutoAddNomenclature}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="nomenclature_search_field"
                    style={{ width: "85%", marginBottom: 0, marginInlineEnd: 0 }}
                  >
                    <NomenclatureSearchField
                      api={api}
                      token={token}
                      select={handleAutoAddNomenclature}
                      nomDS={[]}
                    />
                  </Form.Item>
                </Compact>
              </Form.Item>
            </div>
            
            {fields.map((field, index) => (
              <Form.Item key={field.key} name={[field.name, "nomenclature_id"]} hidden>
                <Input />
              </Form.Item>
            ))}

            <Table
              components={components}
              columns={editableColumns}
              dataSource={tableData}
              pagination={false}
              rowKey="key"
              bordered
              locale={{
                emptyText: "Нет добавленных позиций. Выберите номенклатуру выше, чтобы добавить позицию.",
              }}
            />
          </Card>
          );
        }}
      </Form.List>
            ),
          },
        ]}
      />
    </Form>
  );
}